import { findParentPath } from '../../utils'
import type { IMerchTemplate, IMerchTemplateFileMapping, IMerchTemplateOption } from './IMerchTemplate'
import type CatalogDetails from '@/models/catalogDetails'
import { merchConstants } from '@/models/constants'
import utils from '@/services/utils'
import type MyArticle from '@/models/myArticle'
import type Merch from '@/modules/merch/services/merch'
import techSheetImage from '@/assets/templateLayoutImages/techSheet.png'

class TechSheet implements IMerchTemplate {
  id = merchConstants.slideGenTemplatesId.techSheet
  name = 'Tech Sheet'
  layoutImage = techSheetImage
  slideWidth: number = 1280
  slideHeight: number = 720
  imageHeight: number = 500
  imageWidth: number = 500
  font: string = 'Arial'
  imageDefaultScaleFactor: number = merchConstants.slideImageDefaultScaleFactors.large
  borderProps = {
    height: 24,
    color: '#c82a26',
  }

  staticDataProp = {
    height: 200,
    width: 400,
    color: '#c82a26',
  }

  imageTopPadding = 100
  imageLeftPadding = 100
  logoWidth = 100
  logoHeight = 100
  isMerch: boolean = true

  getOptions(catalog: CatalogDetails, myAttributes: Record<string, IMyAttribute>, defaultImageTypeForTemplates: string): IMerchTemplateOption[] {
    const attributes: IMyAttribute[] = []
    for (const key in myAttributes) {
      attributes.push(myAttributes[key])
    }

    // TODO: Implement configurable options
    const options: IMerchTemplateOption[] = []

    const availableSlideSizes: IKeyLabel[] = []
    for (const key in merchConstants.slideSize) {
      availableSlideSizes.push({ key, label: merchConstants.slideSize[key] })
    }
    options.push({
      name: 'slideSize',
      label: 'generateDialog.steps.options.slideSize',
      type: 'list',
      default: 'wideScreen',
      required: true,
      multiple: false,
      data: availableSlideSizes,
      valueProp: 'key',
      displayProp: 'label',
    })

    // options.push({
    //   name: 'includeInactiveArticles',
    //   label: 'generateDialog.options.includeInactiveArticles',
    //   type: 'bool',
    //   default: false,
    //   required: false,
    // })
    options.push({
      name: 'generateOneSlidePerStyle',
      label: 'generateDialog.steps.options.oneSlidePerStyle',
      type: 'bool',
      default: true,
      required: false,
    })

    options.push({
      name: 'imageType',
      label: 'generateDialog.steps.options.imageType',
      type: 'list',
      default: defaultImageTypeForTemplates,
      data: merchConstants.defaultImageType,
      required: false,
      multiple: false,
      valueProp: 'key',
      displayProp: 'value',
    })
    // if(utils.isValidStringValue(catalog.Config.ArIndicatorAttribute) && utils.isDefined(myAttributes[catalog.Config.ArIndicatorAttribute])) {
    //   options.push({
    //     name: 'addARIndicator',
    //     label: 'generateFrameDialog.steps.options.addARIndicator',
    //     type: 'bool',
    //     default: true,
    //     required: false,
    //   })
    // }

    // if(utils.isValidStringValue(catalog.Config.GhlIndicatorAttribute) && utils.isDefined(myAttributes[catalog.Config.GhlIndicatorAttribute])) {
    //   options.push({
    //     name: 'addGHLIndicator',
    //     label: 'generateFrameDialog.steps.options.addGHLIndicator',
    //     type: 'bool',
    //     default: true,
    //     required: false,
    //   })
    // }

    // if(utils.isValidStringValue(catalog.Config.BAndTIndicatorAttribute) && utils.isDefined(myAttributes[catalog.Config.BAndTIndicatorAttribute])) {
    //   options.push({
    //     name: 'addBAndTIndicator',
    //     label: 'generateFrameDialog.steps.options.addBAndTIndicator',
    //     type: 'bool',
    //     default: true,
    //     required: false,
    //   })
    // }

    return options
  }

  getFileMapping(): IMerchTemplateFileMapping[] {
    const mapping: IMerchTemplateFileMapping[] = []

    mapping.push({
      name: 'articleNumber',
      column: 'ArticleNumber',
      type: 'articleNumber',
      label: 'generateFrameDialog.steps.mapping.articleNumber',
      required: true,
      autoMap: ['article', 'articlenumber', 'article number', 'artnbr', 'style color code', 'colorwaynumber', 'colorway number'],
    })
    return mapping
  }

  async generate(catalog: CatalogDetails, articlesData: MyArticle[], options: Record<string, any>, merch: Merch | undefined): Promise<(void | number)> {
    const data: MyArticle[] = []
    articlesData.forEach((article) => {
      data.push(article)
    })
    return await this.buildTemplate(catalog, data, options, merch)
  }

  async buildTemplate(catalog, articlesData, options, merch) {
    this.isMerch = true
    this.imageDefaultScaleFactor = merchConstants.slideImageDefaultScaleFactors.large
    const promises: Promise<void | number>[] = []
    let ttlSlides = 0
    const currentObjects: any[] = []
    // Set slide size as per user selected
    this.changePropsAsPerSlideSize(options.slideSize)
    if (utils.isDefined(merch)) {
      this.isMerch = true
    }
    else {
      this.isMerch = false
    }
    if (options.generateOneSlidePerStyle) {
      // sort to get lowest sort order article by model
      articlesData.sort((a, b) => {
        const aModelNumberLower = utils.isDefined(a.ModelNumber) ? a.ModelNumber.toLowerCase() : a.ModelNumber
        const bModelNumberLower = utils.isDefined(b.ModelNumber) ? b.ModelNumber.toLowerCase() : b.ModelNumber
        if (aModelNumberLower < bModelNumberLower) {
          return -1
        }
        else if (aModelNumberLower > bModelNumberLower) {
          return 1
        }
        else {
          if (a.SortOrder !== b.SortOrder) {
            return a.SortOrder - b.SortOrder
          }
          else {
            const aLower = utils.isDefined(a.ArticleNumber) ? a.ArticleNumber.toLowerCase() : a.ArticleNumber
            const bLower = utils.isDefined(b.ArticleNumber) ? b.ArticleNumber.toLowerCase() : b.ArticleNumber
            if (aLower < bLower) {
              return -1
            }
            else if (aLower > bLower) {
              return 1
            }
            else {
              return 0
            }
          }
        }
      })
    }
    const modelNumberSet = new Set()
    for (let index = 0; index < articlesData.length; index++) {
      const article = articlesData[index]
      if (options.generateOneSlidePerStyle) {
        if (modelNumberSet.has(article.ModelNumber)) {
          continue
        }
        modelNumberSet.add(article.ModelNumber)
      }
      ttlSlides++
      this.onNewPage(currentObjects)
      currentObjects.push(...this.addArticleData(article))
      // const imageType = options.imageType
      //   if (article._images.length) {
      //     if (options.imageType !== 'newestImage' && options.imageType !== 'firstImage') {
      //       const imageTypeExists = article._images.find(image => image.imageName === options.imageType)
      //       if (!utils.isDefined(imageTypeExists)) {
      //         imageType = catalogData.defaultImageType
      //       }
      //     }
      //   }
      //   else {
      //     imageType = ''
      //   }
      currentObjects.push(this.createImage(article, options.imageType))
      currentObjects.push(...this.addArrowsWthEditableTextBoxes())
      if (this.isMerch) {
        promises.push(this.createSlide(merch, article.ModelName, currentObjects.slice(), this.imageDefaultScaleFactor, options, article.Id)
          .catch((error) => {
          // TODO:Merch@andre ask andre what needs to be done when failed creating slide due to system unable to create slide objects file
            console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
          }))
      }
    //   else {
    //     this.createSlideObjects(currentObjects.slice(), ttlSlides, allSlides)
    //   }
    }
    return Promise.all(promises)
      .then(() => {
        if (this.isMerch) {
          return ttlSlides
        }
      })
  }

  createSlide(merch, name, objects, imageDefaultScaleFactor, options, articleId) {
    let newName = name && name !== '' ? name : 'Slide'
    const folderNode = options.folder
    const sortOrder = Math.max(...folderNode.children.map(child => child.sortOrder), 0) + 1
    const folderStructure = findParentPath(folderNode)
    if (utils.isDefined(folderNode.children.find(node => node.label === name))) {
      let c = 0
      do {
        c++
        newName = `${name} (${c})`
      } while (folderNode.children.find(node => node.label === newName) !== undefined)
    }
    return merch!.addSlide(folderStructure.id, folderStructure.name, folderNode.sortOrder, newName, objects, options.slideSize, imageDefaultScaleFactor, sortOrder, true, [articleId])
  }

  changePropsAsPerSlideSize(slideSize: string) {
    this.slideHeight = merchConstants.slideSizeCanvasDimensions[slideSize].height
    this.slideWidth = merchConstants.slideSizeCanvasDimensions[slideSize].width
    if (slideSize === merchConstants.slideSize.wideScreen) {
      this.imageHeight = 500
      this.imageWidth = 500
    }
    else if (slideSize === merchConstants.slideSize.A3) {
      this.imageHeight = 1000
      this.imageWidth = 1000
    }
    else if (slideSize === merchConstants.slideSize.A4) {
      this.imageHeight = 500
      this.imageWidth = 500
    }
  }

  onNewPage(currentObjects) {
    currentObjects.length = 0
    // add border to the slide
    currentObjects.push(...this.getBorder())
    // add logo to the slide
    currentObjects.push(this.getHeaderLogo())
    // add static text to the slide
    currentObjects.push(...this.getStaticDataObjects())
  }

  addArrowsWthEditableTextBoxes() {
    // add lines to the image
    const dividedImageHeightWise = this.imageHeight / 5
    const dividedImageWidthWise = this.imageWidth / 3.5

    const firstTop = this.borderProps.height + this.imageTopPadding + dividedImageHeightWise / 2
    const secondTop = firstTop + dividedImageHeightWise / 4
    const thirdTop = secondTop + dividedImageHeightWise / 2
    const fourthTop = thirdTop + dividedImageHeightWise
    const leftForLeftSideArrows = this.borderProps.height + this.imageLeftPadding + dividedImageWidthWise / 3
    const leftForRightSideArrows = this.borderProps.height + this.imageLeftPadding + this.imageWidth - dividedImageWidthWise
    return [
      this.addLine(firstTop, leftForRightSideArrows, -50, 200, '#c82a26', false, false),
      this.createStaticTextObj('Enter text here', firstTop - 95, leftForRightSideArrows + 30, 11, 'normal', '#000000', 100, 100, false, false),

      this.addLine(secondTop, leftForLeftSideArrows, 59, 225, '#c82a26', false, false),
      this.createStaticTextObj('Enter text here', secondTop - 115, leftForLeftSideArrows - 75, 11, 'normal', '#000000', 100, 100, false, false),

      this.addLine(thirdTop + 115 + 45, leftForRightSideArrows, 36, 260, '#c82a26', false, false),
      this.createStaticTextObj('Enter text here', thirdTop + 115 + 45 + 80, leftForRightSideArrows + 100, 11, 'normal', '#000000', 100, 100, false, false),

      this.addLine(fourthTop + 55, leftForLeftSideArrows, 50, 210, '#c82a26', false, false),
      this.createStaticTextObj('Enter text here', fourthTop - 40, leftForLeftSideArrows - 110, 11, 'normal', '#000000', 100, 100, false, false),
    ]
  }

  addArticleData(article) {
    const textLeft = this.slideWidth - this.borderProps.height - this.staticDataProp.width
    const textHeaderTop = this.slideHeight - this.borderProps.height - this.staticDataProp.height - 25

    return [
      this.addLine(textHeaderTop - 50, textLeft + 100, 0, 200),
      this.addLine(textHeaderTop - 100, textLeft + 100, 90, 100),
      this.createStaticTextObj('Style Number', textHeaderTop - 140, textLeft, 11, 'bold', '#000000', 100, 100),
      this.createLabel(textHeaderTop - 120, textLeft, ['ModelNumber'], 100, 20, article, 11, 'normal', false),
      this.createStaticTextObj('Price', textHeaderTop - 140, textLeft + 110, 11, 'bold', '#000000', 100, 100),
      this.createLabel(textHeaderTop - 120, textLeft + 110, ['_RetailPrice'], 100, 20, article, 11, 'normal', false),
      this.addLine(textHeaderTop - 150, textLeft + 100, 0, 200),
      this.createStaticTextObj('Enter text here', textHeaderTop - 190, textLeft, 12, 'normal', '#000000', 300, 100, false, false),
      this.createLabel(textHeaderTop - 210, textLeft, ['ModelName'], 300, 30, article, 15, 'bold', false),
    ]
  }

  addLine(top, left, angle, width, color = '#000000', templateObject = true, locked = true) {
    return {
      type: merchConstants.objectTypes.line,
      top,
      left,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      stroke: color,
      strokeWidth: 3,
      strokeDashArray: null,
      locked,
      width,
      height: 1,
      angle,
      templateObject,
    }
  }

  getStaticDataObjects() {
    const left = this.slideWidth - this.borderProps.height - this.staticDataProp.width / 2
    const box = {
      type: merchConstants.objectTypes.rectangle,
      top: this.slideHeight - this.borderProps.height - this.staticDataProp.height / 2,
      left,
      angle: 0,
      width: this.staticDataProp.width,
      height: this.staticDataProp.height,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      fill: this.staticDataProp.color,
      stroke: this.staticDataProp.color,
      locked: true,
      templateObject: true,
    }
    const textLeft = this.slideWidth - this.borderProps.height - this.staticDataProp.width + 20
    const textTop = this.slideHeight - this.borderProps.height - this.staticDataProp.height + 20
    const textHeaderTop = this.slideHeight - this.borderProps.height - this.staticDataProp.height - 25
    return [box, this.createStaticTextObj('WHAT YOU REALLY NEED TO KNOW', textHeaderTop - 7, textLeft + 15, 12, 'bold', '#C82A26'), this.getInfoLogo(textLeft, textHeaderTop), this.createStaticTextObj('Enter text here', textTop, textLeft, 11, 'bold', '#F9ECE4', 350, 200, false, false), this.createStaticTextObj('Enter text here', textTop + 15, textLeft, 11, 'normal', '#F9ECE4', 350, 200, false, false),
    ]
  }

  getBorder() {
    const top = {
      type: merchConstants.objectTypes.rectangle,
      top: this.borderProps.height / 2,
      left: this.slideWidth / 2,
      angle: 0,
      width: this.slideWidth,
      height: this.borderProps.height,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      fill: this.borderProps.color,
      stroke: this.borderProps.color,
      locked: true,
      templateObject: true,
    }
    const left = {
      type: merchConstants.objectTypes.rectangle,
      top: this.slideHeight / 2,
      left: this.borderProps.height / 2,
      angle: 0,
      width: this.borderProps.height,
      height: this.slideHeight,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      fill: this.borderProps.color,
      stroke: this.borderProps.color,
      locked: true,
      templateObject: true,
    }
    const bottom = {
      type: merchConstants.objectTypes.rectangle,
      top: this.slideHeight - this.borderProps.height / 2,
      left: this.slideWidth / 2,
      angle: 0,
      width: this.slideWidth,
      height: this.borderProps.height,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      fill: this.borderProps.color,
      stroke: this.borderProps.color,
      locked: true,
      templateObject: true,
    }
    const right = {
      type: merchConstants.objectTypes.rectangle,
      top: this.slideHeight / 2,
      left: this.slideWidth - this.borderProps.height / 2,
      angle: 0,
      width: this.borderProps.height,
      height: this.slideHeight,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      fill: this.borderProps.color,
      stroke: this.borderProps.color,
      locked: true,
      templateObject: true,
    }

    return [top, left, bottom, right]
  }

  createStaticTextObj(text, top, left, fontSize, fontWeight, fontColor, width = 325, height = 200, templateObject = true, locked = true) {
    return {
      type: merchConstants.objectTypes.textBox,
      top,
      left,
      width,
      height,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      text,
      fill: fontColor,
      backgroundColor: '',
      fontFamily: 'Arial',
      fontSize,
      fontStyle: 'normal',
      fontWeight,
      textAlign: 'left',
      locked,
      templateObject,
      isOriginTopLeft: true,
    }
  }

  createImage(article, imageType: string) {
    const top = 100 + this.imageHeight / 2
    return {
      type: merchConstants.objectTypes.articleImage,
      top,
      left: 100 + this.imageWidth / 2,
      width: this.imageWidth,
      height: this.imageHeight,
      displayWidth: this.imageWidth,
      displayHeight: this.imageHeight,
      articleId: article.Id,
      angle: 0,
      scaleX: 0.9,
      scaleY: 0.9,
      flipX: false,
      flipY: false,
      locked: false,
      templateObject: false,
      shouldAllowTemplateBigImagesToResize: true,
      imageType,
      isRequest: article._IsRequestArticle,
    }
  }

  createLabel(top, left, attributes, width, height, article, fontSize, fontWeight, showLabels = true) {
    if (!attributes || attributes.length === 0) { return }

    return {
      type: merchConstants.objectTypes.articleDetails,
      top,
      left,
      width,
      height,
      angle: 0,
      scaleX: 1,
      scaleY: 1,
      fill: '#000000',
      backgroundColor: '',
      flipX: false,
      flipY: false,
      customOptions: {
        articleProps: attributes.slice(),
        articleBLCoords: { x: 0, y: 0 },
      },
      fontFamily: this.font,
      fontSize,
      fontStyle: 'normal',
      fontWeight,
      textAlign: 'left',
      locked: false,
      articleId: article.Id,
      templateObject: false,
      showLabels,
      isOriginTopLeft: true,
      isRequest: article._IsRequestArticle,
    }
  }

  getHeaderLogo() {
    return {
      type: merchConstants.objectTypes.image,
      top: 32,
      left: this.slideWidth - this.borderProps.height - this.logoWidth / 2,
      width: this.logoWidth,
      height: this.logoHeight,
      angle: 0,
      scaleY: 0.7,
      scaleX: 0.7,
      flipX: false,
      flipY: false,
      locked: true,
      templateObject: true,
      data:
      'data:image/png;base64,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',
      // src: 'static/ua-logo.png'
    }
  }

  getInfoLogo(left, top) {
    return {
      type: merchConstants.objectTypes.image,
      top,
      left,
      width: 30,
      height: 30,
      angle: 0,
      scaleX: 0.64,
      scaleY: 0.64,
      flipX: false,
      flipY: false,
      locked: true,
      templateObject: true,
      data:
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABUAAAAaCAYAAABYQRdDAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsQAAA7EAZUrDhsAAAHwSURBVEhL7ZTNbhJRFIC/ufPDDDMMP2KloZVURKmRAm1ZWIzW1IUbN30Cn8+F72BiXLjQR6ALUxO1uihNMcKMZwwIFKYkDe76JZM7d+beb86cc+/VQoElo4btUrmWLp/p6sttGAykHfYXoklYEpfSort/TEgHBGff6X14y2nnhP55n1CbHDpChv+dYWKWd/BqFZxiZuqXJ6QBQfcrv96/5subd5x9/slAgp5BRmuaAW6RzOErbjyp466lpiKd+IBCWS6JcgXT6ROcHNPrdOgdHc1ex98Y6CtYxQJmOjkljJgulEhV+SnZdgO3lEfXFZrkbHxJ7gwbPbeO9+wlfrWAldKHk8dcqH7UdUjuPSfVqGJnjah2Y6SjuXnMe3vkX2yRyLozUUZckEZoqPwmXmsXv1ZCD4PhYoiSmcRY38Q/OCB1y0E35ynnSgUrjX2/SWq3ibtqSw1FOAjQciWchy0yrQqGLamY74yRymN9pYxTa5FulDCULAOVxLpblw9t4615IowxCjFSQaK1Nh7gP27j5nRUtoLb3JFc38GM+e0R8dIo2nQBe+sRmfY2/v4+fr1K8qY3fB/P5Yd0KOu1+4PzTx/5bayS2LiNXcjG5nLEwpM/HIj4tEuoDJRtywaR3bSAhdKrcElOr861dPn8Byn8AbaUh1iB0h7lAAAAAElFTkSuQmCC',
      // src: 'static/ua-logo.png'
    }
  }
}

export default new TechSheet()
